<script>
  import { tick } from 'svelte';
  import {
    AlertTriangle, CheckCircle2, Copy, Download, FilePlus2, Link2, Save, Trash2,
  } from '../lib/icons/index.js';
  import ErrorBanner from './ErrorBanner.svelte';
  import SuccessBanner from './SuccessBanner.svelte';
  import InfoHint from './InfoHint.svelte';
  import Spinner from './Spinner.svelte';
  import SideSheet from './SideSheet.svelte';
  import { isaApi } from '../stores/api.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { formatDateTime } from '../lib/labels.js';
  import { formatMoney } from '../lib/bilancio.js';
  import {
    ISA_ESITO_BADGE, annoModelloOf, asksTipologia, hasUnsavedEdits, periodiIsa, periodoIsaState, recordReadOnly, copyCalculated, declaredPayload, draftFromDeclared, formatIsaValue,
    isQuadroOpen, isaPermissions, modelsBySector, parseIsaInput, parseVe50, rigoLabel, uncataloguedCount, valuesForModel, ve50AfterReload, ve50Text, visibleRows,
  } from '../lib/isa.js';

  /** Il bilancio aperto nella pagina: il periodo d'imposta è il suo esercizio. */
  export let companyId;
  export let bilancio;
  /** Il ruolo nell'azienda attiva (`/capabilities`). */
  export let role = null;

  $: permissions = isaPermissions(role);
  // A record of a periodo without ISA is read-only, whatever the role: the server refuses every update.
  $: readOnly = recordReadOnly(record);
  $: canWrite = permissions.write && !readOnly;
  $: canConfirm = permissions.confirm && !readOnly;
  $: year = bilancio?.fiscalYear;
  // ISA exist from periodo 2018 and only for a closed periodo; the model is named by the year after.
  $: periodoState = periodoIsaState(year);
  $: annoModello = annoModelloOf(year);
  $: range = periodiIsa();

  let loading = true;
  let error = null;
  let actionError = null;
  let success = '';
  let busy = false;

  /** La scheda del periodo, se esiste; altrimenti si mostra il solo calcolo. */
  let record = null;
  let confronto = null;
  let calcolo = null;
  let draft = {};
  let dirty = false;
  let fieldErrors = {};
  let onlyDifferences = false;
  let ve50Input = '';
  let ve50Error = '';
  let removing = false;
  /** Before a record exists: the model and tipologia chosen by hand; null lets the server settle the model. */
  let chosenCodice = null;
  let chosenTipologia = 'impresa';
  /** The year's models, loaded when the user asks to change the model. */
  let modelIndex = null;
  /** A model change of a saved record, waiting for the user to confirm what it drops. */
  let pendingModel = null;

  $: view = confronto ?? calcolo;
  $: rows = view ? view.quadri.flatMap((quadro) => quadro.righe) : [];
  $: fields = new Map(rows.map((row) => [row.code, row]));
  $: riepilogo = confronto?.riepilogo ?? null;
  $: testi = view?.testi ?? {};
  $: linkedElsewhere = Boolean(record && record.bilancioId && record.bilancioId !== bilancio?.id);
  $: unlinked = Boolean(record && !record.bilancioId);
  $: failedChecks = (confronto?.controlli ?? []).filter((check) => !check.ok);
  $: modello = view?.modello ?? null;
  // The VE50 field is typed apart from the filed values: it is unsaved too until it equals the record's.
  $: ve50Dirty = Boolean(record) && ve50Input !== ve50Text(record);
  $: unsaved = hasUnsavedEdits({ dirty, ve50Input, record });
  $: currentTipologia = record?.tipologiaReddito ?? view?.tipologia ?? chosenTipologia;
  // Reading a model is a read: anyone may look at another one; changing a saved record's is a write.
  $: canChoose = record ? canWrite : permissions.read;
  $: showTipologia = asksTipologia(modello) || currentTipologia === 'lavoro_autonomo';

  let loadedFor = '';
  $: if (companyId && bilancio?.id) load(`${companyId}:${bilancio.id}`);

  async function load(key) {
    if (key === loadedFor) return;
    loadedFor = key;
    loading = true;
    error = null;
    actionError = null;
    success = '';
    record = null;
    confronto = null;
    calcolo = null;
    chosenCodice = null;
    chosenTipologia = 'impresa';
    modelIndex = null;
    pendingModel = null;
    try {
      const { dichiarazioni } = await isaApi.list(companyId);
      if (key !== loadedFor) return;
      const found = (dichiarazioni ?? []).find((entry) => entry.periodoImposta === year) ?? null;
      // A record already filed stays readable; without one, nothing is computed for a periodo without ISA.
      if (found) await loadConfronto({ key, id: found.id });
      else if (periodoState === 'ammesso') await loadCalcolo(key);
    } catch (err) {
      if (key === loadedFor) error = err;
    } finally {
      if (key === loadedFor) loading = false;
    }
  }

  /** The computation without a record: the model chosen by hand, else the one the bilancio's ATECO code gives. */
  async function loadCalcolo(key = loadedFor) {
    const response = await isaApi.calcolo(companyId, {
      periodo: year, bilancioId: bilancio.id, codiceIsa: chosenCodice, tipologia: chosenTipologia,
    });
    if (key === loadedFor) calcolo = response.calcolo;
  }

  /**
   * Il confronto della scheda; `keepDraft` non tocca i valori che l'utente sta
   * scrivendo. Il VE50 in corso di scrittura resta comunque, salvo dopo il suo
   * salvataggio (`resetVe50`). La scheda e il suo confronto si assegnano
   * insieme: la pagina non mostra mai una scheda senza il suo confronto.
   */
  async function loadConfronto({ key = loadedFor, keepDraft = false, resetVe50 = false, id = record?.id } = {}) {
    const response = await isaApi.confronto(companyId, id);
    if (key !== loadedFor) return;
    const previous = record;
    record = response.dichiarazione;
    confronto = response.confronto;
    calcolo = null;
    const map = new Map(response.confronto.quadri.flatMap((quadro) => quadro.righe).map((row) => [row.code, row]));
    if (!keepDraft || !dirty) {
      draft = draftFromDeclared(map, record.valoriDichiarati);
      dirty = false;
      fieldErrors = {};
    }
    const typed = ve50Input;
    ve50Input = ve50AfterReload({ input: ve50Input, previous, next: record, reset: resetVe50 });
    // A note about the VE50 belongs to what was typed: it goes when that does.
    if (ve50Input !== typed) ve50Error = '';
  }

  async function run(action) {
    busy = true;
    actionError = null;
    success = '';
    try {
      await action();
    } catch (err) {
      actionError = err;
    } finally {
      busy = false;
    }
  }

  const startRecord = () => run(async () => {
    const codiceAttivita = calcolo?.modello?.ateco
      ?? calcolo?.quadri.flatMap((quadro) => quadro.righe).find((row) => row.code === 'codice_attivita')?.valore;
    let created;
    try {
      const response = await isaApi.create(companyId, {
        periodoImposta: year, codiceIsa: calcolo.codiceIsa, tipologiaReddito: calcolo.tipologia ?? chosenTipologia, bilancioId: bilancio.id,
        ...(codiceAttivita ? { codiceAttivita } : {}),
      });
      created = response.dichiarazione;
    } catch (err) {
      // Someone else created it meanwhile: open theirs.
      if (err.code !== 'isa_esiste') throw err;
      const { dichiarazioni } = await isaApi.list(companyId);
      created = dichiarazioni.find((entry) => entry.periodoImposta === year) ?? null;
      if (!created) throw err;
    }
    await loadConfronto({ id: created.id });
    success = tImmediate('bilancio.isa.done.created', { year });
  });

  const openModelChoice = () => run(async () => {
    const response = await isaApi.modelli(companyId, { anno: year, tipologia: currentTipologia });
    modelIndex = response.modelli ?? [];
  });

  /**
   * Another model. Without a record it is only a different computation. A
   * saved record keeps only the values of righi the new model has: what it
   * would drop is shown first, and nothing changes until the user confirms.
   */
  function chooseModel(codiceIsa, tipologia = currentTipologia) {
    if (!codiceIsa) return undefined;
    if (!record) {
      chosenCodice = codiceIsa;
      chosenTipologia = tipologia;
      return run(() => loadCalcolo());
    }
    if (codiceIsa === record.codiceIsa && tipologia === record.tipologiaReddito) return undefined;
    return run(() => preparePending(codiceIsa, tipologia));
  }

  /** What a model change of the saved record keeps and drops, from the values as saved. */
  async function preparePending(codiceIsa, tipologia) {
    const { modello: target } = await isaApi.modello(companyId, codiceIsa, { anno: year, tipologia });
    const { valori, tolti } = valuesForModel(record.valoriDichiarati, { fields, codiceIsa: record.codiceIsa, anno: record.periodoImposta }, target);
    pendingModel = { codiceIsa, tipologia, descrizione: target.descrizione, valori, tolti };
  }

  /** Back to the values as saved, the VE50 included: the explicit way out of unsaved edits before a model change. */
  function discardEdits() {
    draft = draftFromDeclared(fields, record.valoriDichiarati);
    dirty = false;
    fieldErrors = {};
    ve50Input = ve50Text(record);
    ve50Error = '';
  }

  function chooseTipologia(tipologia) {
    // For a code under two models the tipologia picks the model; otherwise the model stays and the server checks it.
    const codiceIsa = modello?.perTipologia?.[tipologia] ?? (record ? record.codiceIsa : null);
    modelIndex = null;
    if (!record) {
      chosenTipologia = tipologia;
      chosenCodice = codiceIsa;
      return run(() => loadCalcolo());
    }
    return chooseModel(codiceIsa, tipologia);
  }

  const confirmModel = () => run(async () => {
    // The change is computed from the saved values: unsaved edits would be lost without a word.
    if (unsaved) return;
    const { codiceIsa, tipologia, valori } = pendingModel;
    await isaApi.update(companyId, record.id, { codiceIsa, tipologiaReddito: tipologia, valoriDichiarati: valori });
    pendingModel = null;
    modelIndex = null;
    dirty = false;
    await loadConfronto();
    success = tImmediate('bilancio.isa.done.modelChanged', { codice: codiceIsa });
  });

  const linkThisBilancio = () => run(async () => {
    await isaApi.update(companyId, record.id, { bilancioId: bilancio.id });
    await loadConfronto({ keepDraft: true });
    success = tImmediate('bilancio.isa.done.linked');
  });

  function onInput(row, value) {
    draft = { ...draft, [row.code]: value };
    dirty = true;
    if (fieldErrors[row.code]) validate(row);
  }

  function validate(row) {
    const parsed = parseIsaInput(row, draft[row.code]);
    fieldErrors = { ...fieldErrors, [row.code]: parsed.ok ? null : parsed.reason };
  }

  function copyFromCalculated() {
    const result = copyCalculated(rows, draft);
    draft = result.draft;
    if (result.copied) dirty = true;
    success = result.copied
      ? tImmediate('bilancio.isa.done.copied', { n: result.copied })
      : tImmediate('bilancio.isa.done.nothingToCopy');
  }

  const saveValues = () => run(async () => {
    const payload = declaredPayload(fields, draft);
    if (!payload.ok) {
      fieldErrors = { ...fieldErrors, [payload.code]: payload.reason };
      onlyDifferences = false;
      await tick();
      document.getElementById(`isa-input-${cssId(payload.code)}`)?.focus();
      return;
    }
    await isaApi.update(companyId, record.id, { valoriDichiarati: payload.valori });
    dirty = false;
    await loadConfronto();
    // A model change waiting for confirmation is recomputed from the values just saved.
    if (pendingModel) await preparePending(pendingModel.codiceIsa, pendingModel.tipologia);
    success = tImmediate('bilancio.isa.done.saved');
  });

  const setCoverage = (event) => {
    const checked = event.currentTarget.checked;
    return run(async () => {
      // A confirmation names the periodo and the bilancio it speaks of, as shown
      // here: if the record moved meanwhile the server refuses it as stale.
      const body = checked
        ? { coperturaFattureConfermata: true, periodoImposta: record.periodoImposta, bilancioId: record.bilancioId ?? null }
        : { coperturaFattureConfermata: false };
      try {
        await isaApi.update(companyId, record.id, body);
      } catch (err) {
        if (err.code === 'isa_conferma_non_attuale') await loadConfronto({ keepDraft: true });
        throw err;
      }
      await loadConfronto({ keepDraft: true });
      success = tImmediate(checked ? 'bilancio.isa.done.confirmed' : 'bilancio.isa.done.unconfirmed');
    }).finally(() => {
      // A refused change leaves the box as the record says, not as clicked.
      if (record) record = { ...record };
    });
  };

  const saveVe50 = () => {
    const value = parseVe50(ve50Input);
    if (value === undefined) {
      ve50Error = tImmediate('bilancio.isa.copertura.ve50Invalid');
      return undefined;
    }
    ve50Error = '';
    return run(async () => {
      await isaApi.update(companyId, record.id, { volumeAffariDichiarazioneIva: value });
      await loadConfronto({ keepDraft: true, resetVe50: true });
      success = tImmediate('bilancio.isa.done.ve50Saved');
    });
  };

  const removeRecord = () => run(async () => {
    await isaApi.remove(companyId, record.id);
    removing = false;
    record = null;
    confronto = null;
    draft = {};
    dirty = false;
    await loadCalcolo();
    success = tImmediate('bilancio.isa.done.removed', { year });
  });

  const downloadCsv = () => run(async () => {
    const { blob, filename } = await isaApi.downloadCsv(companyId, record.id);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  });

  const quadroDifferences = (righe) => righe.filter((row) => ['diverso', 'vicino'].includes(row.esito)).length;
  const cssId = (code) => String(code).replace(/[^A-Za-z0-9_-]/g, '-');
  const words = () => ({ yes: tImmediate('bilancio.isa.si'), no: tImmediate('bilancio.isa.no') });
  const valueText = (row, value) => formatIsaValue(row, value, words());
  /** The figure a row compares, with its interval when it rests on rounded facts. */
  const calculatedText = (row) => {
    if (row.vuoto) return tImmediate('bilancio.isa.vuoto');
    return valueText(row, row.valore);
  };
  const differenceText = (row) => {
    if (row.differenza === null || row.differenza === undefined) return '—';
    if (row.unita === 'euro') return formatMoney(row.differenza, { signed: true });
    if (['percentuale', 'aliquota', 'decimale'].includes(row.unita)) return tImmediate('bilancio.isa.punti', { n: String(row.differenza).replace('.', ',') });
    return row.differenza;
  };
  const hintOf = (row) => [row.fonteTesto, ...(row.avvisi ?? []).map((note) => note.testo)].filter(Boolean).join(' — ');
  const INPUT_MODES = { euro: 'decimal', aliquota: 'decimal', decimale: 'decimal', percentuale: 'numeric', numero: 'numeric', codice_attivita: 'numeric', lista: 'numeric' };
</script>

<section aria-labelledby="isa-title">
  <div class="section-heading">
    <h2 id="isa-title">
      {periodoState === 'prima_degli_isa'
        ? $t('bilancio.isa.titleSenzaIsa', { periodo: year })
        : $t('bilancio.isa.title', { annoModello, periodo: year })}
    </h2>
  </div>

  <details class="disclosure mb-6">
    <summary><span class="disclosure-label">{$t('bilancio.isa.intro.toggle')}</span></summary>
    <div class="disclosure-content isa-intro measure-wide">
      <h3 class="font-bold text-heading text-sm">{$t('bilancio.isa.intro.whatTitle')}</h3>
      <p class="text-sm">{$t('bilancio.isa.intro.what')}</p>
      <h3 class="font-bold text-heading text-sm">{$t('bilancio.isa.intro.doesTitle')}</h3>
      <p class="text-sm">{$t('bilancio.isa.intro.does')}</p>
      <h3 class="font-bold text-heading text-sm">{$t('bilancio.isa.intro.statusTitle')}</h3>
      <p class="text-sm">{$t('bilancio.isa.intro.status')}</p>
      <h3 class="font-bold text-heading text-sm">{$t('bilancio.isa.intro.doesNotTitle')}</h3>
      <p class="text-sm">{$t('bilancio.isa.intro.doesNot')}</p>
    </div>
  </details>

  {#if loading}
    <Spinner />
  {:else if error}
    <ErrorBanner {error} title={$t('bilancio.isa.loadError')} />
  {:else if periodoState !== 'ammesso' && !view}
    <div class="card p-5 mb-8 isa-periodo" role="note">
      <h3 class="font-bold text-heading">{$t(`bilancio.isa.periodo.${periodoState}.title`, { periodo: year, annoModello })}</h3>
      <p class="mt-2 text-sm measure-wide">
        {$t(`bilancio.isa.periodo.${periodoState}.body`, { periodo: year, annoModello, ...range })}
      </p>
    </div>
  {:else if view}
    {#if periodoState !== 'ammesso'}
      <div class="alert alert-warning mb-6 isa-periodo" role="note">
        <div class="min-w-0 flex-1">
          <p class="alert-title">{$t(`bilancio.isa.periodo.${periodoState}.title`, { periodo: year, annoModello })}</p>
          <p class="alert-message mt-1">{$t(`bilancio.isa.periodo.${periodoState}.body`, { periodo: year, annoModello, ...range })}</p>
          {#if readOnly}<p class="alert-message mt-1">{$t('bilancio.isa.periodo.solaLettura')}</p>{/if}
        </div>
      </div>
    {/if}
    <div class="alert alert-info mb-6" role="note">
      <div class="min-w-0 flex-1">
        <p class="alert-title">{$t('bilancio.isa.avvertenzaTitle')}</p>
        <p class="alert-message mt-1">{view.avvertenza}</p>
      </div>
    </div>

    <SuccessBanner message={success} />
    <ErrorBanner error={actionError} />

    {#if view.avvisi?.length}
      <ul class="isa-notes mb-6">
        {#each view.avvisi as note (note.code)}
          <li class="text-sm"><AlertTriangle size={14} class="isa-note-mark" aria-hidden="true" />{note.testo}</li>
        {/each}
      </ul>
    {/if}

    <article class="card p-5 mb-8 isa-modello" aria-labelledby="isa-modello-title">
      <h3 id="isa-modello-title" class="font-bold text-heading inline-flex items-center gap-1">
        {periodoState === 'prima_degli_isa'
          ? $t('bilancio.isa.titleSenzaIsa', { periodo: year })
          : $t('bilancio.isa.modello.title', { annoModello, periodo: year })}<InfoHint text={$t('bilancio.isa.modello.help')} />
      </h3>
      {#if view.codiceIsa}
        <p class="mt-2"><span class="mono">{view.codiceIsa}</span>{modello?.descrizione ? ` – ${modello.descrizione}` : ''}</p>
      {:else}
        <p class="mt-2 text-sm">{$t('bilancio.isa.modello.none')}</p>
      {/if}
      {#if modello?.origineTesto}<p class="mt-1 text-xs text-muted measure-wide">{modello.origineTesto}</p>{/if}

      {#if showTipologia}
        <fieldset class="mt-4 isa-tipologie">
          <legend class="label inline-flex items-center gap-1">
            {$t('bilancio.isa.modello.tipologiaLabel')}<InfoHint text={$t('bilancio.isa.modello.tipologiaHelp')} />
          </legend>
          {#each ['impresa', 'lavoro_autonomo'] as tipologia (tipologia)}
            <label class="isa-choice text-sm">
              <input type="radio" name="isa-tipologia" value={tipologia} checked={(pendingModel?.tipologia ?? currentTipologia) === tipologia}
                disabled={!canChoose || busy} on:change={() => chooseTipologia(tipologia)} />
              {testi.tipologie?.[tipologia] ?? tipologia}
            </label>
          {/each}
        </fieldset>
      {/if}

      {#if canChoose}
        {#if modelIndex}
          <label class="label mt-4" for="isa-modello-select">{$t('bilancio.isa.modello.changeLabel')}</label>
          <select id="isa-modello-select" class="input isa-model-select" value={view.codiceIsa ?? ''} disabled={busy}
            on:change={(event) => chooseModel(event.currentTarget.value)}>
            <option value="" disabled>{$t('bilancio.isa.modello.placeholder')}</option>
            {#each modelsBySector(modelIndex) as group (group.settore)}
              <optgroup label={testi.settori?.[group.settore] ?? group.settore}>
                {#each group.modelli as entry (entry.codiceIsa)}
                  <option value={entry.codiceIsa}>{entry.codiceIsa} – {entry.descrizione}</option>
                {/each}
              </optgroup>
            {/each}
          </select>
        {:else}
          <button type="button" class="btn btn-secondary btn-sm mt-4" disabled={busy} on:click={openModelChoice}>
            {$t('bilancio.isa.modello.change')}
          </button>
        {/if}
      {/if}

      {#if pendingModel}
        <div class="alert alert-warning mt-4" role="alert">
          <AlertTriangle size={18} class="alert-mark mt-0.5" aria-hidden="true" />
          <div class="min-w-0 flex-1">
            <p class="alert-message">
              {pendingModel.tolti.length
                ? $t('bilancio.isa.modello.changeWarning', { codice: pendingModel.codiceIsa, n: pendingModel.tolti.length })
                : $t('bilancio.isa.modello.changeWarningNone', { codice: pendingModel.codiceIsa })}
            </p>
            {#if pendingModel.tolti.length}
              <ul class="isa-dropped mt-2 text-sm">
                {#each pendingModel.tolti as dropped (dropped.code)}
                  <li><span class="mono">{dropped.code}</span> – {dropped.descrizione}</li>
                {/each}
              </ul>
            {/if}
            {#if unsaved}
              <p class="alert-message mt-3 font-bold" role="status">{$t('bilancio.isa.modello.dirty')}</p>
            {/if}
            <div class="flex flex-wrap gap-2 mt-3">
              {#if dirty}
                <button type="button" class="btn btn-secondary btn-sm gap-2" disabled={busy} on:click={saveValues}>
                  <Save size={14} aria-hidden="true" />{$t('bilancio.isa.toolbar.save')}
                </button>
              {/if}
              {#if ve50Dirty}
                <button type="button" class="btn btn-secondary btn-sm gap-2" disabled={busy} on:click={saveVe50}>
                  <Save size={14} aria-hidden="true" />{$t('bilancio.isa.modello.saveVe50')}
                </button>
              {/if}
              {#if unsaved}
                <button type="button" class="btn btn-secondary btn-sm" disabled={busy} on:click={discardEdits}>{$t('bilancio.isa.modello.discard')}</button>
              {/if}
              <button type="button" class="btn btn-primary btn-sm" disabled={busy || unsaved} on:click={confirmModel}>{$t('bilancio.isa.modello.changeConfirm')}</button>
              <button type="button" class="btn btn-secondary btn-sm" disabled={busy} on:click={() => (pendingModel = null)}>{$t('bilancio.isa.modello.changeCancel')}</button>
            </div>
          </div>
        </div>
      {/if}
    </article>

    {#if !confronto}
      <div class="card p-5 mb-8 isa-start">
        <h3 class="font-bold text-heading">{$t('bilancio.isa.start.title')}</h3>
        <p class="mt-2 text-sm measure-wide">{$t('bilancio.isa.start.body', { year })}</p>
        {#if canWrite}
          <button type="button" class="btn btn-primary gap-2 mt-4" disabled={busy || !view.codiceIsa} on:click={startRecord}>
            <FilePlus2 size={16} aria-hidden="true" />
            {$t('bilancio.isa.start.action')}
          </button>
          {#if !view.codiceIsa}<p class="mt-2 text-xs text-muted">{$t('bilancio.isa.start.needsModel')}</p>{/if}
        {:else}
          <p class="mt-3 text-xs text-muted">{$t('bilancio.isa.start.readonly')}</p>
        {/if}
      </div>
    {:else}
      {#if linkedElsewhere || unlinked}
        <div class="alert alert-warning mb-6" role="status">
          <AlertTriangle size={18} class="alert-mark mt-0.5" aria-hidden="true" />
          <div class="min-w-0 flex-1">
            <p class="alert-message">{$t(linkedElsewhere ? 'bilancio.isa.link.elsewhere' : 'bilancio.isa.link.none', { year })}</p>
            {#if canWrite}
              <button type="button" class="btn btn-secondary btn-sm mt-3 gap-2" disabled={busy} on:click={linkThisBilancio}>
                <Link2 size={14} aria-hidden="true" />
                {$t('bilancio.isa.link.action')}
              </button>
            {/if}
          </div>
        </div>
      {/if}

      <div class="isa-panels mb-8">
        <article class="card p-5" aria-labelledby="isa-copertura-title">
          <h3 id="isa-copertura-title" class="font-bold text-heading inline-flex items-center gap-1">
            {$t('bilancio.isa.copertura.title')}<InfoHint text={$t('bilancio.isa.copertura.help')} />
          </h3>
          {#if readOnly}
            <!-- A record of a periodo without ISA: what was filed, as filed, nothing to change. -->
            <p class="mt-2 text-sm">
              {record.coperturaFattureConfermata && record.coperturaConfermataIl
                ? $t('bilancio.isa.copertura.confirmedOn', { date: formatDateTime(record.coperturaConfermataIl) })
                : $t('bilancio.isa.copertura.nonConfermata')}
            </p>
            <dl class="mt-4">
              <dt class="label">{$t('bilancio.isa.copertura.ve50Label')}</dt>
              <dd class="isa-figure">{record.volumeAffariDichiarazioneIva ? formatMoney(record.volumeAffariDichiarazioneIva) : '—'}</dd>
            </dl>
          {:else}
            <p class="mt-2 text-sm">{confronto.copertura.motivo}</p>

            <label class="isa-confirm mt-4">
              <input type="checkbox" checked={record.coperturaFattureConfermata} disabled={!canConfirm || busy}
                on:change={setCoverage} />
              <span class="text-sm">{testi.copertura?.dichiarazione}</span>
            </label>
            {#if record.coperturaFattureConfermata && record.coperturaConfermataIl}
              <p class="mt-2 text-xs text-muted inline-flex items-center gap-1">
                <CheckCircle2 size={14} aria-hidden="true" />{$t('bilancio.isa.copertura.confirmedOn', { date: formatDateTime(record.coperturaConfermataIl) })}
              </p>
            {/if}
            {#if !canConfirm}
              <p class="mt-2 text-xs text-muted">{$t('bilancio.isa.copertura.readonly')}</p>
            {/if}

            <form class="mt-5" on:submit|preventDefault={saveVe50} novalidate>
              <label class="label inline-flex items-center gap-1" for="isa-ve50">
                {$t('bilancio.isa.copertura.ve50Label')}<InfoHint text={$t('bilancio.isa.copertura.ve50Help')} />
              </label>
              <div class="flex flex-wrap items-end gap-3">
                <input id="isa-ve50" class="input isa-money-input" type="text" inputmode="decimal" bind:value={ve50Input}
                  disabled={!canWrite || busy} aria-invalid={ve50Error ? 'true' : undefined}
                  aria-describedby={ve50Error ? 'isa-ve50-error' : undefined} />
                {#if canWrite}
                  <button type="submit" class="btn btn-secondary btn-sm" disabled={busy}>{$t('bilancio.isa.copertura.ve50Save')}</button>
                {/if}
              </div>
              {#if ve50Error}<p id="isa-ve50-error" class="field-error mt-1">{ve50Error}</p>{/if}
              {#if confronto.copertura.ve50Testo}<p class="mt-2 text-xs text-muted">{confronto.copertura.ve50Testo}</p>{/if}
            </form>
          {/if}
        </article>

        {#if riepilogo}
          <article class="card p-5" aria-labelledby="isa-riepilogo-title">
            <h3 id="isa-riepilogo-title" class="font-bold text-heading">{$t('bilancio.isa.riepilogo.title')}</h3>
            <dl class="isa-counts mt-3">
              {#each ['coincide', 'vicino', 'diverso', 'non_verificabile', 'solo_dichiarato', 'non_dichiarato', ...(riepilogo.esiti.non_catalogato ? ['non_catalogato'] : [])] as esito}
                <div>
                  <dt><span class="badge {ISA_ESITO_BADGE[esito]}">{testi.esiti?.[esito]}</span></dt>
                  <dd class="isa-count">{riepilogo.esiti[esito] ?? 0}</dd>
                </div>
              {/each}
            </dl>
            {#if failedChecks.length}
              <ul class="mt-4 isa-notes">
                {#each failedChecks as check (check.id)}
                  <li class="text-xs">
                    <AlertTriangle size={14} class="isa-note-mark" aria-hidden="true" />{$t('bilancio.isa.riepilogo.totale', { gruppo: check.id, totale: check.totale })}
                  </li>
                {/each}
              </ul>
            {/if}
          </article>
        {/if}
      </div>

      <div class="isa-toolbar mb-4">
        <label class="isa-filter text-sm">
          <input type="checkbox" bind:checked={onlyDifferences} />
          {$t('bilancio.isa.toolbar.onlyDifferences')}
        </label>
        <div class="isa-toolbar-actions">
          {#if canWrite}
            <button type="button" class="btn btn-secondary btn-sm gap-2" disabled={busy} on:click={copyFromCalculated}>
              <Copy size={14} aria-hidden="true" />{$t('bilancio.isa.toolbar.copy')}
            </button>
            <button type="button" class="btn btn-primary btn-sm gap-2" disabled={busy || !dirty} on:click={saveValues}>
              <Save size={14} aria-hidden="true" />{$t('bilancio.isa.toolbar.save')}
            </button>
          {/if}
          <button type="button" class="btn btn-secondary btn-sm gap-2" disabled={busy} on:click={downloadCsv}>
            <Download size={14} aria-hidden="true" />{$t('bilancio.isa.toolbar.csv')}
          </button>
        </div>
      </div>
      {#if dirty}<p class="mb-4 text-xs text-muted" role="status">{$t('bilancio.isa.toolbar.unsaved')}</p>{/if}
      {#if canWrite}<p class="mb-6 text-xs text-muted measure-wide">{$t('bilancio.isa.toolbar.copyHelp')}</p>{/if}
    {/if}

    {#each view.quadri as quadro (quadro.quadro)}
      {@const shown = visibleRows(quadro.righe, onlyDifferences)}
      {#if shown.length}
        <details class="disclosure isa-quadro mb-6" open={isQuadroOpen(quadro.quadro, shown, onlyDifferences)}>
          <summary>
            <span class="disclosure-label">
              <h3 id="isa-quadro-{quadro.quadro}" class="isa-quadro-title">{quadro.etichetta}</h3>
              <span class="tab-count">{shown.length}</span>
              {#if quadroDifferences(shown)}
                <span class="badge badge-yellow">{$t('bilancio.isa.quadroDifferences', { n: quadroDifferences(shown) })}</span>
              {/if}
              {#if uncataloguedCount(shown)}
                <span class="badge {ISA_ESITO_BADGE.non_catalogato}">{$t('bilancio.isa.quadroNonCatalogati', { n: uncataloguedCount(shown) })}</span>
              {/if}
            </span>
          </summary>
          <div class="disclosure-content table-wrap isa-table-wrap">
            <table class="table-modern isa-table w-full">
              <thead class="table-header">
                <tr>
                  <th class="table-cell table-header-cell text-left" scope="col">{$t('bilancio.isa.columns.rigo')}</th>
                  <th class="table-cell table-header-cell text-left" scope="col">{$t('bilancio.isa.columns.descrizione')}</th>
                  <th class="table-cell table-header-cell text-right" scope="col">{$t('bilancio.isa.columns.calcolato')}</th>
                  {#if confronto}
                    <th class="table-cell table-header-cell text-right" scope="col">{$t('bilancio.isa.columns.dichiarato')}</th>
                    <th class="table-cell table-header-cell text-right" scope="col">{$t('bilancio.isa.columns.differenza')}</th>
                    <th class="table-cell table-header-cell text-left" scope="col">{$t('bilancio.isa.columns.esito')}</th>
                  {/if}
                  <th class="table-cell table-header-cell text-left" scope="col">{$t('bilancio.isa.columns.perche')}</th>
                </tr>
              </thead>
              <tbody>
                {#each shown as row (row.code)}
                  <tr class="isa-row">
                    <th scope="row" class="table-cell isa-rigo mono" class:isa-rigo-campo={!row.rigo}>{#each rigoLabel(row).split('_') as part, index}{#if index}_<wbr />{/if}{part}{/each}</th>
                    <td class="table-cell isa-descrizione" data-label={$t('bilancio.isa.columns.descrizione')}>
                      <span>
                        {row.descrizione}
                        <span class="isa-status">{row.statoTesto}</span>
                      </span>
                    </td>
                    <td class="table-cell text-right isa-figure" data-label={$t('bilancio.isa.columns.calcolato')}>
                      <span class="inline-flex items-center justify-end gap-1">
                        <span class:text-muted={row.statoEffettivo === 'N_indicativo'}>{calculatedText(row)}</span>
                        {#if hintOf(row)}<InfoHint text={hintOf(row)} />{/if}
                      </span>
                      {#if row.intervallo}
                        <span class="isa-detail">{$t('bilancio.isa.intervallo', { min: formatMoney(row.intervallo.min), max: formatMoney(row.intervallo.max) })}</span>
                      {/if}
                      {#if row.valoreEsatto && row.valore !== null}
                        <span class="isa-detail">{$t('bilancio.isa.esatta', { value: String(row.valoreEsatto).replace('.', ',') })}</span>
                      {/if}
                      {#if row.suggerimento}
                        <span class="isa-detail">{$t('bilancio.isa.suggerimento', { value: formatMoney(row.suggerimento.valore) })}</span>
                      {/if}
                    </td>
                    {#if confronto}
                      <td class="table-cell text-right isa-figure" data-label={$t('bilancio.isa.columns.dichiarato')}>
                        {#if canWrite && row.inseribile}
                          {#if row.unita === 'casella'}
                            <input id="isa-input-{cssId(row.code)}" type="checkbox" checked={draft[row.code] === true}
                              disabled={busy} aria-label={$t('bilancio.isa.inputLabel', { rigo: rigoLabel(row) || row.descrizione })}
                              on:change={(event) => onInput(row, event.currentTarget.checked)} />
                          {:else}
                            <input id="isa-input-{cssId(row.code)}" class="input isa-input" type="text"
                              inputmode={INPUT_MODES[row.unita] ?? 'text'}
                              value={draft[row.code] ?? ''} disabled={busy}
                              aria-label={$t('bilancio.isa.inputLabel', { rigo: rigoLabel(row) || row.descrizione })}
                              aria-invalid={fieldErrors[row.code] ? 'true' : undefined}
                              aria-describedby={fieldErrors[row.code] ? `isa-error-${cssId(row.code)}` : undefined}
                              on:input={(event) => onInput(row, event.currentTarget.value)} on:blur={() => validate(row)} />
                          {/if}
                          {#if row.valori}
                            <span class="isa-detail">{$t('bilancio.isa.valoriAmmessi', { valori: row.valori })}</span>
                          {/if}
                          {#if fieldErrors[row.code]}
                            <span id="isa-error-{cssId(row.code)}" class="field-error isa-detail">{$t(`bilancio.isa.errors.${fieldErrors[row.code]}`)}</span>
                          {/if}
                        {:else}
                          {valueText(row, row.dichiarato)}
                        {/if}
                      </td>
                      <td class="table-cell text-right isa-figure" data-label={$t('bilancio.isa.columns.differenza')}>{differenceText(row)}</td>
                      <td class="table-cell" data-label={$t('bilancio.isa.columns.esito')}>
                        {#if row.esito}
                          <span class="badge {ISA_ESITO_BADGE[row.esito] ?? 'badge-gray'}">{row.esitoTesto}</span>
                        {/if}
                      </td>
                    {/if}
                    <td class="table-cell isa-perche" data-label={$t('bilancio.isa.columns.perche')}>
                      <span class="text-xs text-muted">{row.spiegazione}</span>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </details>
      {/if}
    {/each}

    {#if confronto && onlyDifferences && !rows.some((row) => ['diverso', 'vicino'].includes(row.esito))}
      <p class="text-sm text-muted mb-8">{$t('bilancio.isa.toolbar.noDifferences')}</p>
    {/if}

    {#if confronto && permissions.remove}
      <div class="mt-4">
        <button type="button" class="btn btn-secondary btn-sm gap-2 hover:text-danger" disabled={busy} on:click={() => (removing = true)}>
          <Trash2 size={14} aria-hidden="true" />{$t('bilancio.isa.remove.action')}
        </button>
      </div>
    {/if}
  {/if}
</section>

{#if permissions.remove}
  <SideSheet open={removing} id="isa-remove" eyebrow={$t('bilancio.isa.remove.eyebrow')}
    title={$t('bilancio.isa.remove.title', { year })} closeLabel={$t('bilancio.isa.remove.cancel')}
    on:close={() => { if (!busy) removing = false; }}>
    <p class="sheet-section-intro">{$t('bilancio.isa.remove.body')}</p>
    <svelte:fragment slot="footer">
      <button type="button" class="btn btn-secondary" disabled={busy} on:click={() => (removing = false)}>{$t('bilancio.isa.remove.cancel')}</button>
      <button type="button" class="btn btn-danger gap-2" disabled={busy} on:click={removeRecord}>
        <Trash2 size={16} aria-hidden="true" />{$t('bilancio.isa.remove.confirm')}
      </button>
    </svelte:fragment>
  </SideSheet>
{/if}

<style>
  .isa-intro h3 + p { margin-top: 4px; }
  .isa-intro p + h3 { margin-top: 14px; }

  .isa-notes { display: grid; gap: 6px; padding: 0; list-style: none; }
  .isa-notes li { display: flex; align-items: flex-start; gap: 6px; }
  .isa-notes :global(.isa-note-mark) { flex: none; margin-top: 3px; color: var(--color-warning); }

  .isa-panels {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 22rem), 1fr));
  }
  .isa-confirm { display: flex; align-items: flex-start; gap: 10px; cursor: pointer; }
  .isa-confirm input { flex: none; margin-top: 3px; }
  .isa-money-input { width: 12rem; }
  .isa-tipologie { display: grid; gap: 6px; border: 0; margin-inline: 0; padding: 0; }
  .isa-choice { display: flex; align-items: flex-start; gap: 8px; cursor: pointer; }
  .isa-choice input { flex: none; margin-top: 3px; }
  .isa-model-select { max-width: 40rem; }
  .isa-dropped { display: grid; gap: 2px; max-height: 12rem; overflow-y: auto; padding-left: 1.1rem; list-style: disc; }

  .isa-counts {
    display: grid;
    gap: 10px 16px;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  }
  .isa-counts div { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .isa-count {
    color: var(--color-text-heading);
    font-variant-numeric: var(--wd-figures-tabular);
  }

  .isa-toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; }
  .isa-toolbar-actions { display: flex; flex-wrap: wrap; gap: 8px; }
  .isa-filter { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; }

  .isa-quadro-title { display: inline; font-size: inherit; font-weight: inherit; }
  .isa-quadro summary .disclosure-label { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 8px; }
  .isa-table-wrap { max-width: 100%; overflow-x: auto; }
  .isa-row { border-bottom: var(--wd-border-width-hairline) solid var(--table-border); }
  .isa-row th, .isa-row td { vertical-align: top; white-space: normal; }
  .isa-rigo { width: 5.5rem; color: var(--color-text-heading); font-weight: normal; text-align: left; white-space: nowrap; }
  .isa-rigo-campo { white-space: normal; }
  .isa-descrizione { min-width: 12rem; color: var(--color-text-body); }
  .isa-status {
    display: block;
    margin-top: 2px;
    color: var(--color-text-muted);
    font-size: 0.75rem;
  }
  .isa-figure {
    min-width: 8rem;
    color: var(--color-text-heading);
    font-variant-numeric: var(--wd-figures-tabular);
  }
  .isa-detail { display: block; color: var(--color-text-muted); font-size: 0.75rem; }
  .isa-input { width: 8rem; text-align: right; }
  .isa-perche { min-width: 11rem; }

  /* Phones: one block per rigo, each value with its column named. */
  @media (width < 42.5rem) {
    .isa-table thead {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }
    .isa-table, .isa-table tbody { display: block; width: 100%; }
    .isa-table tr { display: block; padding: 10px 0; }
    .isa-table th[scope='row'] { display: block; width: auto; padding: 0 4px 4px; }
    .isa-table td {
      display: grid;
      grid-template-columns: minmax(6.5rem, 38%) minmax(0, 1fr);
      gap: 0 10px;
      min-width: 0;
      padding: 4px;
      text-align: left;
    }
    .isa-table td::before {
      content: attr(data-label);
      color: var(--color-text-muted);
      font-size: 12px;
    }
    .isa-table .isa-figure span { justify-content: flex-start; }
    .isa-input { width: 100%; text-align: left; }
  }
</style>
